import {
  Action,
  Card,
  closeCurrentDialog,
  closeDialog,
  openConfirmDialog,
  openDialog,
  Separator,
  TabProps,
  Tabs,
} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {always, head, isNil, mergeAll} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {
  GetWarehousesResponse,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
} from '@dms/api/warehouse';
import {
  GetMetadaServiceCaseApiResponse,
  PostMaterialBasketItemForPriceListFeApiArg,
  PostMaterialBasketItemForPriceListFeApiResponse,
  PostMaterialBasketItemForWarehouseApiArg,
  PostMaterialBasketItemForWarehouseApiResponse,
  useGetItemsBasketActionsQuery,
  useGetServiceOrderQuery,
  usePutItemsBasketActionMutation,
  WarehouseAction,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  ArticleFolderTree,
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  ItemData,
  ItemInformation,
  logError,
  PriceListExternalFilters,
  PriceListRowData,
  refreshDmsDatagrid,
  WarehouseTabExternalFilters,
  usePermissions,
  useWarehouseDataGridTreeFolder,
  WarehouseAvailability,
  WarehouseAvailabilityQueryModifier,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {ArticleCreate, getSingleOrUndefined, PriceListDetail} from '@dms/shared-aftersales';

import {buildArray, composePath, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ExternalSystemStateType} from '../types/externalSystemStateType';
import {OpenXentryLink} from './OpenXentryLink';

const EXTERNAL_SYSTEM_ADD_KEY = 'xentry-xhpi-cart';
const EXTERNAL_SYSTEM_UPLOAD_KEY = 'xentry-xhpi-inquiry';
const DSMATERIAL_EXTERNAL_FILTER_ID = 'dsmaterial-external-filter';
const PRICE_LIST_EXTERNAL_FILTER_ID = 'price-list-external-filter';
const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

const SPACE_AROUND_DG_FROM_VH_IN_REM = 8.6;

const TABS_IDS = {
  warehouseTab: 'serviceOrder-warehouseTab',
  priceListTab: 'serviceOrder-priceListTab',
};

const CREATE_ARTICLE_DIALOG = 'createArticleDialog';
const WAREHOUSE_AVAILABILITY_DIALOG = 'warehouseAvailabilityDialog';

interface MaterialListCardProps extends TestIdProps {
  onExternalSystemChange: (state: ExternalSystemStateType) => void;
  onWarehouseArticleAdd: (
    arg: PostMaterialBasketItemForWarehouseApiArg
  ) => Promise<void | PostMaterialBasketItemForWarehouseApiResponse>;
  onPriceListItemAdd: (
    arg: PostMaterialBasketItemForPriceListFeApiArg
  ) => Promise<void | PostMaterialBasketItemForPriceListFeApiResponse>;
  shouldResetSmartSearch: boolean;
  availableWarehouses: GetWarehousesResponse;
  serviceCase: GetMetadaServiceCaseApiResponse;
}

export function MaterialListCard(props: MaterialListCardProps) {
  const {serviceCaseId, orderId, addMaterialRequestJobId} = useWorkshopUrl();

  const branchId = props.serviceCase?.branchId;
  const authorizationProfileId = props.serviceCase?.authorizationProfileId;

  const navigate = useNavigate();

  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [dataGridFilters, setDataGridFilters] = useState<Record<string, string>>({});

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder({
    availableWarehouses: props.availableWarehouses,
  });

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {branchId, authorizationProfileId}]);

  const {data} = useGetItemsBasketActionsQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addMaterialRequestJobId,
    basketType: 'material-basket',
  });
  const [putItemsBasketAction, {isLoading: isPutItemsLoading}] = usePutItemsBasketActionMutation();

  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId,
    serviceOrderId: orderId,
  });

  const [getArticleIdByManufacturerNumberAndManufacturerId] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const externalSystemAddOption = data?.itemsBasketActions?.options?.find(
    (option) => option?.key === EXTERNAL_SYSTEM_ADD_KEY
  );
  const externalSystemUploadOption = data?.itemsBasketActions?.options?.find(
    (option) => option?.key === EXTERNAL_SYSTEM_UPLOAD_KEY
  );

  const addMaterialQueryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([
        filter,
        {
          serviceOrderId: orderId,
          serviceCaseId,
          serviceJobId: addMaterialRequestJobId,
        },
      ]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [orderId, serviceCaseId, addMaterialRequestJobId, dataGridModifier]
  );

  const onAddClick = () => {
    putItemsBasketAction({
      basketType: 'material-basket',
      body: {actionKey: EXTERNAL_SYSTEM_ADD_KEY},
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
    })
      .unwrap()
      .then((response) => {
        openDialog(
          <OpenXentryLink
            xentryLink={response?.xentry_xhpi_cart?.xhpiUrl}
            onClose={closeCurrentDialog}
          />,
          {size: 'small'}
        );
        props.onExternalSystemChange('PREPARED');
      })
      .catch(handleApiError);
  };

  const onUploadClick = () => {
    openConfirmDialog({
      text: i18n.t('entity.addMaterial.lables.uploadFromXentry'),
      onConfirm: () =>
        putItemsBasketAction({
          basketType: 'material-basket',
          body: {actionKey: EXTERNAL_SYSTEM_UPLOAD_KEY},
          serviceCaseId,
          serviceOrderId: orderId,
          serviceJobId: addMaterialRequestJobId,
        })
          .unwrap()
          .then(() => props.onExternalSystemChange('UPLOAD_SCCESS'))
          .catch((error: Error) =>
            handleApiError(error, {callback: () => props.onExternalSystemChange('UPLOAD_ERROR')})
          ),
    });
  };

  const handleSendToBasket = async (articleId: string, actionKey?: string) => {
    if (actionKey) {
      const warehouseAction = match<string, WarehouseAction | null>(actionKey)
        .with('nonBinding', always('non-binding'))
        .with('dispatch', always('dispatch-request'))
        .with('request', always('request'))
        .otherwise(always(null));

      if (warehouseAction) {
        await props.onWarehouseArticleAdd({
          serviceCaseId,
          serviceOrderId: orderId,
          serviceJobId: addMaterialRequestJobId,
          warehouseAction,
          body: {warehouseArticleId: articleId},
        });
        closeCurrentDialog();
        return;
      }
    }

    await props.onPriceListItemAdd({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addMaterialRequestJobId,
      body: {warehouseArticleId: articleId},
    });

    closeCurrentDialog();
  };

  const handleOpenWarehouseAvailabilityDialog = (
    itemInformation: ItemInformation,
    queryModifier: WarehouseAvailabilityQueryModifier
  ) => {
    const handleOnItemClick = async (item: ItemData) => {
      const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
        warehouseId: item.warehouseId,
        manufacturerNumber: item.catalogNumber,
        manufacturerId: item.manufacturerId,
      })
        .unwrap()
        .catch(handleApiError);

      const articleId = articleIdResponse?.articleId;

      if (articleId) {
        handleSendToBasket(articleId, item.actionKey);
        return;
      }

      openDialog(
        <ArticleCreate
          defaultValues={{
            warehouseId: item.warehouseId,
            catalogNumber: item.catalogNumber,
            manufacturerId: item.manufacturerId,
            name: itemInformation.name,
          }}
          hasAddToBasketButton
          onAddToBasket={(articleId) => {
            handleSendToBasket(articleId, item.actionKey);
            closeCurrentDialog();
          }}
          onArticleCreate={(createdArticleData) => {
            handleSendToBasket(createdArticleData.articleId, item.actionKey);
            closeCurrentDialog();
          }}
          onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
          data-testid={testIds.workshop.serviceOrders('dialogs.createArticle')}
        />,
        {
          id: CREATE_ARTICLE_DIALOG,
          title: i18n.t('entity.warehouse.labels.createNewArticle'),
          withAdditionalFooter: true,
          scrollBehavior: 'outside',
        }
      );
    };

    openDialog(
      <WarehouseAvailability
        onItemClick={handleOnItemClick}
        itemInformation={itemInformation}
        queryModifier={queryModifier}
      />,
      {
        id: WAREHOUSE_AVAILABILITY_DIALOG,
        title: i18n.t('entity.warehouse.labels.warehouseAvailability'),
        scrollBehavior: 'outside',
        size: 'large',
      }
    );
  };

  const onCreateArticleClick = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber:
            'manufacturerNumber' in dataGridFilters
              ? dataGridFilters.manufacturerNumber
              : undefined,
          manufacturerId:
            'manufacturerId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.manufacturerId)
              : undefined,
          warehouseId:
            'warehouseId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.warehouseId)
              : undefined,
          name: 'name' in dataGridFilters ? dataGridFilters.name : undefined,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
              })
            );
          }
          closeCurrentDialog();
          refreshDmsDatagrid('service-case-order-job-add-material');
        }}
        submitButtonTitle={i18n.t('general.actions.create')}
        onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
        hasCreateAndEditButton
        data-testid={testIds.workshop.serviceOrders('dialogs.createArticle')}
      />,
      {
        id: CREATE_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        withAdditionalFooter: true,
        scrollBehavior: 'outside',
      }
    );
  };

  const warehouseArticleActionCallback: ActionCallback = ({actionKey, rowId}) => {
    const warehouseArticleId = isArray(rowId) ? head(rowId) : rowId;

    if (!warehouseArticleId) {
      return;
    }

    const warehouseAction = match<string, WarehouseAction | null>(actionKey)
      .with('nonBinding', always('non-binding'))
      .with('dispatch', always('dispatch-request'))
      .with('request', always('request'))
      .otherwise(always(null));

    warehouseAction &&
      props.onWarehouseArticleAdd({
        serviceCaseId,
        serviceOrderId: orderId,
        serviceJobId: addMaterialRequestJobId,
        warehouseAction,
        body: {warehouseArticleId},
      });
  };

  const supplierPriceListActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const priceListId = isArray(rowId) ? head(rowId) : rowId;
    const catalogNumber = getColumnData<string>('catalogNumber')?.value;
    const name = getColumnData<string>('name')?.value;
    const manufacturer = getColumnData<{label: string; id: string}>('manufacturerId')?.value;
    const supplier = getColumnData<{label: string; id: string}>('supplierId')?.value;

    if (!isString(catalogNumber)) {
      return logError('Catalog number is not defined');
    }

    if (!isString(name)) {
      return logError('Name is not defined');
    }

    if (!isString(manufacturer?.id)) {
      return logError('Manufacturer id is not defined');
    }

    if (!isString(manufacturer?.label)) {
      return logError('Manufacturer label is not defined');
    }

    if (!isString(supplier?.label)) {
      return logError('Supplier label is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
    } as PriceListRowData;

    const itemInformation: ItemInformation = {
      catalogNumber,
      name,
      manufacturerLabel: manufacturer.label,
      supplierLabel: supplier.label,
    };

    const queryModifier: WarehouseAvailabilityQueryModifier = {
      variantId: serviceOrder?.serviceOrderVariantId as string,
      originObjectType: 'service-order-case',
      manufacturerId: manufacturer.id,
      catalogNumber,
    };

    match(actionKey)
      .with('addToBasket', () => {
        handleOpenWarehouseAvailabilityDialog(itemInformation, queryModifier);
      })
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const cardActions = buildArray<Action>()
    .when(externalSystemAddOption && externalSystemAddOption.style !== 'hidden', {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t(`entity.addMaterial.actions.addFromExternalSystem`),
      isDisabled: externalSystemAddOption?.style === 'disabled',
      isLoading: externalSystemAddOption?.style === 'enable' && isPutItemsLoading,
      onClick: onAddClick,
    })
    .when(externalSystemUploadOption && externalSystemUploadOption.style !== 'hidden', {
      type: 'button',
      variant: 'link',
      leftIcon: 'file/upload',
      title: i18n.t(`entity.addMaterial.actions.uploadFromExternalSystem`),
      isDisabled: externalSystemUploadOption?.style === 'disabled',
      isLoading: externalSystemUploadOption?.style === 'enable' && isPutItemsLoading,
      onClick: onUploadClick,
    })
    .add({
      type: 'button',
      variant: 'secondary',
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.warehouse.labels.createNewArticle'),
      isDisabled: !articleCreate,
      tooltip: !articleCreate
        ? i18n.t('module.warehouse.label.missingArticleCreatePermission')
        : undefined,
      onClick: onCreateArticleClick,
    });

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      actions: cardActions,
      content: (
        <Box height="100%" width="100%">
          <Card isFullHeight data-testid={suffixTestId('content', props)}>
            <HStack height="100%">
              <Box flex={1} maxHeight="100%" overflow="auto">
                <VStack height="100%">
                  <Box>
                    <div id={DSMATERIAL_EXTERNAL_FILTER_ID}></div>
                  </Box>
                  <Space vertical={8} />
                  <ArticleFolderTree
                    ref={folderTreeRef}
                    data-testid={testIds.warehouse.articleList('articleFolderTree')}
                  />
                </VStack>
              </Box>

              <Separator orientation="vertical" />

              <Box flex={3}>
                <DmsDataGrid
                  // DG must be re-rendered on folder change to update the query modifier
                  // eslint-disable-next-line no-restricted-syntax
                  key={`${dataGridModifier?.key}_${props.shouldResetSmartSearch}`}
                  gridCode="service-case-order-job-add-material"
                  externalFilterId={DSMATERIAL_EXTERNAL_FILTER_ID}
                  onFilterChanged={setDataGridFilters as (filters: unknown) => void}
                  filterComponent={(datagridProps) => (
                    <WarehouseTabExternalFilters
                      data-testid={suffixTestId('dg-external-filters', props)}
                      {...datagridProps}
                    />
                  )}
                  actionCallback={warehouseArticleActionCallback}
                  emptyState={{
                    headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
                  }}
                  queryModifier={addMaterialQueryModifier}
                  _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'CUSTOM'}}
                  data-testid={testIds.workshop.serviceCaseDetail('warehouse-articles')}
                />
              </Box>
            </HStack>
          </Card>
        </Box>
      ),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <Box height={`calc(100vh - ${SPACE_AROUND_DG_FROM_VH_IN_REM}rem)`} width="100%">
          <Card isFullHeight data-testid={suffixTestId('content', props)}>
            <HStack height="100%">
              <Box flex={1}>
                <VStack height="100%">
                  <Box>
                    <div id={PRICE_LIST_EXTERNAL_FILTER_ID}></div>
                  </Box>
                </VStack>
              </Box>

              <Separator orientation="vertical" />

              <Box flex={3}>
                <DmsDataGrid
                  gridCode={WAREHOUSE_GRID_CODES.priceListBasket}
                  actionCallback={supplierPriceListActionCallback}
                  externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
                  filterComponent={PriceListExternalFilters}
                  predefinedColumns={PREDEFINED_COLUMNS}
                  emptyState={{
                    headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
                  }}
                  queryModifier={queryModifier}
                  data-testid={suffixTestId('workCatalogue', props)}
                />
              </Box>
            </HStack>
          </Card>
        </Box>
      ),
    },
  ];

  return (
    <VStack height="100%" grow={1}>
      <Tabs
        variant="fullHeight"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={(id) => setActiveTabId(id)}
        data-testid={testIds.workshop.serviceCaseDetail('tabs')}
        isLazy
        isFullHeight
        lazyBehavior="unmount"
      />
    </VStack>
  );
}
